/* Fantasy Sabers / Impact 6.2 — one free saber per order.
 * This script never sets prices. A Shopify Buy X Get Y discount is required.
 * The pure rules are exported in Node so the cart edge cases can be checked.
 */
(function () {
  'use strict';
  const MARKER = '_fs_free_saber';
  const idOf = item => String(item.variant_id || item.id);
  const managed = item => String(item.properties?.[MARKER] || '') === '1';
  const giftsIn = cart => (cart.items || []).filter(managed);
  const priceOf = item => Number(item.original_price ?? item.price ?? 0);
  const qualifies = (cart, state) => {
    const ids = new Set((state.eligibleVariantIds || []).map(String));
    return (cart.items || []).some(item => !managed(item) && !item.gift_card && item.quantity > 0 && priceOf(item) > 0 && ids.has(idOf(item)));
  };
  const activeAt = (config, now, isDraft) => {
    if (!config.enabled) return false;
    const start = Date.parse(config.start), end = Date.parse(config.end);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return false;
    return (now >= start && now < end) || Boolean(isDraft && config.draftPreview);
  };
  const fingerprint = (cart, state) => {
    const ids = new Set((state.eligibleVariantIds || []).map(String));
    return (cart.items || []).filter(item => !managed(item) && ids.has(idOf(item))).map(item => `${idOf(item)}:${item.quantity}`).sort().join('|');
  };
  const discountCredit = (cart, state, titles) => {
    const names = new Set(titles.filter(Boolean).map(x => x.trim().toLowerCase()));
    const ids = new Set((state.gifts || []).map(item => String(item.id)));
    return (cart.items || []).filter(item => ids.has(idOf(item))).reduce((total, item) => total + (item.line_level_discount_allocations || []).reduce((sum, allocation) => names.has(String(allocation.discount_application?.title || '').trim().toLowerCase()) ? sum + Number(allocation.amount || 0) : sum, 0), 0);
  };
  const giftIsFree = (cart, state, titles) => {
    const owned = giftsIn(cart);
    if (owned.length !== 1 || owned[0].quantity !== 1 || !qualifies(cart, state)) return false;
    const gift = owned[0];
    if (!(state.gifts || []).some(option => String(option.id) === idOf(gift))) return false;
    if (priceOf(gift) <= 0) return false;
    if (Number(gift.final_line_price ?? gift.line_price) === 0) return true;
    // Shopify can allocate the same-value BXGY saving to another eligible line.
    return discountCredit(cart, state, titles) >= priceOf(gift);
  };
  const unmanagedReward = (cart, state, titles) => !giftsIn(cart).length && discountCredit(cart, state, titles) > 0;
  const cleanupFor = (cart, state, active) => {
    const available = new Set((state.gifts || []).filter(gift => gift.available).map(gift => String(gift.id)));
    const allowed = active && qualifies(cart, state);
    const updates = {};
    let kept = false;
    for (const item of giftsIn(cart)) {
      const keep = allowed && !kept && available.has(idOf(item));
      const quantity = keep ? 1 : 0;
      if (keep) kept = true;
      if (item.quantity !== quantity) updates[item.key] = quantity;
    }
    return updates;
  };
  const rules = { MARKER, managed, giftsIn, qualifies, activeAt, fingerprint, discountCredit, giftIsFree, unmanagedReward, cleanupFor };
  if (typeof module !== 'undefined' && module.exports) module.exports = rules;
  if (typeof document === 'undefined') return;

  class FreeSaberOffer {
    constructor(root) {
      this.root = root;
      this.config = JSON.parse(root.querySelector('[data-fs-gift-config]').textContent);
      this.state = JSON.parse(root.querySelector('[data-fs-gift-state]').textContent);
      this.dialog = root.querySelector('dialog');
      this.base = window.Shopify?.routes?.root || '/';
      this.isDraft = Boolean(this.config.designMode || (window.Shopify?.theme?.role && window.Shopify.theme.role !== 'main'));
      this.testCode = this.isDraft ? String(this.config.testCode || '').trim() : '';
      this.titles = [this.config.discountTitle, this.testCode];
      this.selected = null;
      this.queue = Promise.resolve();
      this.working = false;
      this.uncertainCart = false;
      this.pendingOpen = false;
      this.bypassCheckout = false;
      this.clockOffset = Number(this.state.serverTime) - Date.now();
      this.setState(this.state);
      this.bind();
      this.observeCart();
      this.enqueue(async () => {
        await this.reconcile(true);
        if (this.config.pageType === 'cart' && this.shouldOffer()) await this.open(false);
      });
      this.timer = window.setInterval(() => {
        if (this.lastActive !== this.active()) this.enqueue(() => this.reconcile(true));
      }, 30000);
    }
    active() { return activeAt(this.config, Date.now() + this.clockOffset, this.isDraft); }
    get cart() { return this.state.cart; }
    enqueue(task) {
      this.queue = this.queue.catch(() => {}).then(task).catch(error => { this.report(error.message || 'Please refresh your cart and try again.'); });
      return this.queue;
    }
    shouldOffer() {
      return this.active() && qualifies(this.cart, this.state) && !giftsIn(this.cart).length && !unmanagedReward(this.cart, this.state, this.titles) && (this.state.gifts || []).some(gift => gift.available) && !this.dismissed();
    }
    dismissed() {
      try { return sessionStorage.getItem('fs-free-saber-dismissed') === fingerprint(this.cart, this.state); } catch (_) { return false; }
    }
    remember() {
      try { sessionStorage.setItem('fs-free-saber-dismissed', fingerprint(this.cart, this.state)); } catch (_) { /* Storage is optional. */ }
    }
    async request(path, body) {
      // Do not retry mutations automatically: an interrupted response may already have added the item.
      const options = { credentials: 'same-origin', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } };
      if (body !== undefined) { this.uncertainCart = true; options.method = 'POST'; options.headers['Content-Type'] = 'application/json'; options.body = JSON.stringify(body); }
      const response = await fetch(this.base + path, options);
      const data = await response.json();
      if (!response.ok || Number(data.status) >= 400) throw new Error(typeof data.description === 'string' ? data.description : 'This saber could not be added. Please check your cart and try again.');
      return data;
    }
    async refresh() {
      const url = new URL(this.base, window.location.origin);
      url.searchParams.set('section_id', 'fs-gift-state');
      url.searchParams.set('_fs', String(Date.now()));
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('We could not check your gift eligibility. Please refresh your cart and try again.');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const script = doc.querySelector('[data-fs-gift-state]');
      if (!script) throw new Error('We could not check your gift eligibility. Please refresh your cart and try again.');
      const state = JSON.parse(script.textContent);
      if (!state.cart?.items || !Array.isArray(state.gifts)) throw new Error('The cart could not be checked. Please refresh and try again.');
      this.setState(state);
      return state;
    }
    setState(state) {
      this.state = state;
      if (state.serverTime) this.clockOffset = Number(state.serverTime) - Date.now();
      this.lastActive = this.active();
      this.render();
    }
    bundledState(cart) {
      const html = cart?.sections?.['fs-gift-state'];
      if (!html) return null;
      try { return JSON.parse(new DOMParser().parseFromString(html, 'text/html').querySelector('[data-fs-gift-state]').textContent); } catch (_) { return null; }
    }
    async change(updates) {
      if (!Object.keys(updates).length) return;
      await this.request('cart/update.js', { updates }); // Only reduces/removes already-known keys.
      await this.refresh();
    }
    async removeManaged(variantId) {
      await this.refresh();
      const updates = {};
      for (const item of giftsIn(this.cart)) if (!variantId || idOf(item) === String(variantId)) updates[item.key] = 0;
      await this.change(updates);
    }
    async reconcile(publishChanges) {
      await this.refresh();
      const updates = cleanupFor(this.cart, this.state, this.active());
      let changed = Object.keys(updates).length > 0;
      if (changed) await this.change(updates);
      if (giftsIn(this.cart).length && !giftIsFree(this.cart, this.state, this.titles)) {
        await this.removeManaged();
        changed = true;
        this.report('The free-saber discount is no longer available for this cart, so the gift was removed. Your purchased items are still in the cart.');
      }
      this.uncertainCart = false;
      if (changed && publishChanges) await this.publish();
      this.render();
      return changed;
    }
    async publish() {
      if (this.config.pageType === 'cart') { window.location.reload(); return; }
      const drawer = document.getElementById('cart-drawer');
      const section = drawer?.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '');
      if (section) {
        const url = new URL(this.base, window.location.origin);
        url.searchParams.set('sections', section);
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('Your cart changed, but its display could not refresh. Please reload the page.');
        this.cart.sections = await response.json();
      } else this.cart.sections = {};
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: this.cart, fsGiftUpdate: true } }));
      this.render();
    }
    busy(value) {
      this.working = value;
      document.documentElement.classList.toggle('fs-gift-busy', value);
      this.dialog.setAttribute('aria-busy', String(value));
      this.root.querySelectorAll('[data-fs-gift-close]').forEach(button => button.disabled = value);
      this.render();
    }
    report(message) {
      const error = this.root.querySelector('[data-fs-gift-error]');
      error.textContent = message; error.hidden = !message;
      document.querySelectorAll('[data-fs-gift-cart-error]').forEach(node => { node.textContent = message; node.hidden = !message; });
    }
    render() {
      if (!this.dialog) return;
      const active = this.active(), eligible = qualifies(this.cart, this.state), owned = giftsIn(this.cart)[0];
      const claimed = unmanagedReward(this.cart, this.state, this.titles);
      const available = (this.state.gifts || []).filter(gift => gift.available);
      document.documentElement.classList.toggle('fs-gift-hide-express', Boolean(active && this.config.hideExpress));
      this.root.querySelector('[data-fs-gift-preview]').hidden = !(this.isDraft && !activeAt(this.config, Date.now() + this.clockOffset, false));
      this.root.querySelectorAll('[data-fs-gift-variant]').forEach(button => {
        const gift = this.state.gifts.find(item => String(item.id) === button.dataset.fsGiftVariant);
        button.disabled = Boolean(this.working || !gift?.available);
        button.setAttribute('aria-pressed', String(button.dataset.fsGiftVariant === String(this.selected)));
        button.querySelector('[data-fs-gift-stock]').textContent = gift?.available ? 'FREE' : 'Sold out';
      });
      const selected = this.state.gifts.find(gift => String(gift.id) === String(this.selected) && gift.available);
      const add = this.root.querySelector('[data-fs-gift-add]');
      add.disabled = Boolean(this.working || !selected || !eligible || !active || claimed);
      add.textContent = this.working ? 'Updating your cart…' : owned ? 'Update my free saber' : 'Add my free saber';
      const status = this.root.querySelector('[data-fs-gift-status]');
      status.textContent = this.working ? 'Please wait while we update your cart.' : !active ? 'This offer is not currently active.' : !eligible ? 'Add a qualifying saber to your cart to choose a free gift.' : claimed ? 'Your free-saber discount is already applied to an item in your cart.' : !available.length ? 'The free sabers are currently unavailable.' : selected ? `${selected.name} selected · RGB 3.0 · 82cm` : 'Select one free saber.';
      document.querySelectorAll('[data-fs-gift-offer]').forEach(offer => {
        offer.hidden = !(active && eligible && (available.length || owned || claimed));
        const name = owned ? this.state.gifts.find(gift => String(gift.id) === idOf(owned))?.name : null;
        offer.querySelector('[data-fs-gift-cart-title]').textContent = owned || claimed ? 'Your free-saber offer is applied' : this.config.heading || this.root.querySelector('#fs-gift-title').textContent;
        offer.querySelector('[data-fs-gift-cart-message]').textContent = owned ? `${name || 'Your saber'} selected. Your saving is included in the cart total.` : claimed ? 'A saber already in your cart has received the free-saber discount.' : 'One free Deluxe RGB 82cm saber with your saber order.';
        const button = offer.querySelector('[data-fs-gift-open]');
        button.textContent = claimed ? 'View cart' : owned ? 'Change' : 'Choose';
        button.disabled = this.working;
      });
    }
    async open(refresh = true) {
      if (refresh) await this.reconcile(true);
      if (!this.active() || !qualifies(this.cart, this.state)) return;
      if (unmanagedReward(this.cart, this.state, this.titles)) { window.location.href = this.base + 'cart'; return; }
      if (!this.state.gifts.some(gift => gift.available)) return;
      const owned = giftsIn(this.cart)[0];
      this.selected = owned ? idOf(owned) : null;
      this.returnFocus = document.activeElement;
      const drawers = [...document.querySelectorAll('cart-drawer[open],quick-buy-drawer[open],cart-notification-drawer[open]')];
      await Promise.all(drawers.map(drawer => typeof drawer.hide === 'function' ? drawer.hide() : undefined));
      this.report('');
      this.render();
      if (!this.dialog.open) this.dialog.showModal();
      this.root.querySelector('[data-fs-gift-close]')?.focus();
    }
    close() {
      if (this.working) return;
      this.remember();
      if (this.dialog.open) this.dialog.close();
      if (this.returnFocus?.isConnected) this.returnFocus.focus();
    }
    async selectGift() {
      if (this.working || !this.selected) return;
      this.busy(true); this.report('');
      const chosenId = String(this.selected);
      let previous = null, attempted = false, changed = false;
      try {
        changed = await this.reconcile(false);
        if (!this.active() || !qualifies(this.cart, this.state)) throw new Error('Add a qualifying saber to your cart before choosing a gift.');
        if (unmanagedReward(this.cart, this.state, this.titles)) throw new Error('A saber in your cart already has the free-saber discount. Remove that free item first if you want to choose a different gift.');
        const option = this.state.gifts.find(gift => String(gift.id) === chosenId);
        if (!option?.available) throw new Error('That free saber is no longer available. Please choose another.');
        previous = giftsIn(this.cart)[0] || null;
        if (previous && idOf(previous) === chosenId && giftIsFree(this.cart, this.state, this.titles)) {
          this.busy(false); this.close(); return;
        }
        if (previous) { changed = true; await this.removeManaged(); }
        if (this.testCode) {
          // Preserve any other codes already present in this draft session.
          const codes = [...new Set([...(this.cart.discount_codes || []).map(code => code.code), this.testCode].filter(Boolean))];
          await this.request('cart/update.js', { discount: codes.join(',') });
        }
        attempted = true; changed = true;
        await this.request('cart/add.js', { items: [{ id: Number(chosenId), quantity: 1, properties: { [MARKER]: '1' } }] });
        await this.refresh();
        const cleanup = cleanupFor(this.cart, this.state, this.active());
        if (Object.keys(cleanup).length) await this.change(cleanup);
        if (!giftIsFree(this.cart, this.state, this.titles)) throw new Error('The free-saber discount could not be applied. We have not kept the extra saber in your cart. Please try again or contact us.');
        this.uncertainCart = false;
        await this.publish();
        this.busy(false); this.close();
        const drawer = document.getElementById('cart-drawer');
        if (typeof drawer?.show === 'function') drawer.show();
        else if (this.config.pageType !== 'cart') window.location.href = this.base + 'cart';
      } catch (error) {
        // Read fresh keys: Shopify can change line keys when a discount is applied.
        try {
          if (attempted) await this.removeManaged(chosenId);
          if (changed && previous) {
            await this.refresh();
            if (!giftsIn(this.cart).length && this.active() && qualifies(this.cart, this.state) && this.state.gifts.some(gift => String(gift.id) === idOf(previous) && gift.available)) {
              await this.request('cart/add.js', { items: [{ id: Number(idOf(previous)), quantity: 1, properties: { [MARKER]: '1' } }] });
              await this.refresh();
              if (!giftIsFree(this.cart, this.state, this.titles)) await this.removeManaged();
            }
          }
          this.uncertainCart = false;
          if (changed) await this.publish();
          this.report(error.message);
        } catch (_) {
          this.uncertainCart = true;
          this.report('We could not finish updating your gift. Please refresh your cart before checking out.');
        }
      } finally { this.busy(false); }
    }
    async beforeCheckout(resume) {
      this.busy(true);
      try {
        await this.reconcile(true);
        if (giftsIn(this.cart).length && !giftIsFree(this.cart, this.state, this.titles)) throw new Error('Please refresh your cart before checking out.');
        this.busy(false);
        resume();
      } catch (error) { this.report(error.message); }
      finally { this.busy(false); }
    }
    observeCart() {
      const containers = [document.getElementById('cart-drawer'), document.querySelector('.cart-order')].filter(Boolean);
      this.observer = new MutationObserver(() => {
        if (this.renderFrame) return;
        this.renderFrame = requestAnimationFrame(() => { this.renderFrame = null; this.observer.disconnect(); this.render(); containers.filter(node => node.isConnected).forEach(node => this.observer.observe(node, { childList: true, subtree: true })); });
      });
      containers.forEach(node => this.observer.observe(node, { childList: true, subtree: true }));
    }
    bind() {
      document.addEventListener('cart:prepare-bundled-sections', event => {
        if (!event.detail.sections.includes('fs-gift-state') && event.detail.sections.length < 5) event.detail.sections.push('fs-gift-state');
      });
      document.addEventListener('variant:add', event => {
        const state = this.bundledState(event.detail?.cart);
        if (state) this.setState(state);
        const eligibleIds = new Set(this.state.eligibleVariantIds.map(String));
        const eligibleAdded = (event.detail?.items || []).some(item => !managed(item) && eligibleIds.has(idOf(item)));
        if (eligibleAdded && this.shouldOffer()) { event.detail.blockCartDrawerOpening = true; this.pendingOpen = true; }
      }, true);
      document.addEventListener('cart:change', event => {
        if (event.detail?.fsGiftUpdate) return;
        const before = giftsIn(this.cart).length;
        const state = this.bundledState(event.detail?.cart);
        if (state) this.setState(state);
        if (before && !giftsIn(this.cart).length) this.remember();
        const open = this.pendingOpen; this.pendingOpen = false;
        this.enqueue(async () => { await this.reconcile(true); if (open && this.shouldOffer()) await this.open(false); });
      });
      document.addEventListener('cart:refresh', () => this.enqueue(() => this.reconcile(true)));
      window.addEventListener('pageshow', event => { if (event.persisted) this.enqueue(() => this.reconcile(true)); });
      this.root.addEventListener('click', event => {
        const choice = event.target.closest('[data-fs-gift-variant]');
        if (choice && !choice.disabled && !this.working) { this.selected = choice.dataset.fsGiftVariant; this.report(''); this.render(); }
        if (event.target.closest('[data-fs-gift-close]')) this.close();
        if (event.target.closest('[data-fs-gift-add]')) this.enqueue(() => this.selectGift());
      });
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); });
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.close();
      });
      document.addEventListener('click', event => {
        const opener = event.target.closest('[data-fs-gift-open]');
        if (opener) { event.preventDefault(); this.enqueue(() => this.open()); }
        if (this.working && event.target.closest('line-item-quantity')) { event.preventDefault(); event.stopImmediatePropagation(); }
        const checkout = event.target.closest('a[href]');
        if (!checkout || (!this.active() && !giftsIn(this.cart).length && !this.working && !this.uncertainCart)) return;
        const url = new URL(checkout.href, window.location.origin);
        if (url.origin === location.origin && /\/(checkout|checkouts)(\/|$)/.test(url.pathname)) {
          event.preventDefault(); event.stopImmediatePropagation();
          this.enqueue(() => this.beforeCheckout(() => { window.location.href = checkout.href; }));
        }
      }, true);
      document.addEventListener('change', event => {
        if (this.working && event.target.matches('[data-line-key]')) {
          event.preventDefault(); event.stopImmediatePropagation();
          const line = this.cart.items.find(item => item.key === event.target.dataset.lineKey);
          if (line) event.target.value = line.quantity;
        }
      }, true);
      document.addEventListener('submit', event => {
        if (event.submitter?.name !== 'checkout') return;
        if (this.bypassCheckout) { this.bypassCheckout = false; return; }
        if (!this.active() && !giftsIn(this.cart).length && !this.working && !this.uncertainCart) return;
        event.preventDefault(); event.stopImmediatePropagation();
        const form = event.target, submitter = event.submitter;
        this.enqueue(() => this.beforeCheckout(() => { this.bypassCheckout = true; form.requestSubmit(submitter); }));
      }, true);
    }
  }
  const start = () => {
    const root = document.getElementById('fs-free-saber-root');
    if (root && !root.fsGiftController) {
      try { root.fsGiftController = new FreeSaberOffer(root); }
      catch (error) { console.error('Fantasy Sabers gift chooser:', error); }
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})();
